// frontend/src/components/ui/ErrorMessage.jsx
import React from "react";
import Icon from "../AppIcon";
import Button from "./Button";

const ErrorMessage = ({
  title = "Something went wrong",
  message = "An error occurred while loading the content.",
  icon = "AlertTriangle",
  variant = "error", // error, warning, info, success
  showRetry = true,
  retryText = "Try Again",
  onRetry = null,
  fullScreen = false,
  className = "",
  actions = [],
}) => {
  const variantClasses = {
    error: {
      container:
        "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800",
      icon: "text-red-500 dark:text-red-400",
      title: "text-red-800 dark:text-red-300",
      message: "text-red-700 dark:text-red-400",
      button: "bg-red-600 hover:bg-red-700 text-white",
    },
    warning: {
      container:
        "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800",
      icon: "text-yellow-500 dark:text-yellow-400",
      title: "text-yellow-800 dark:text-yellow-300",
      message: "text-yellow-700 dark:text-yellow-400",
      button: "bg-yellow-600 hover:bg-yellow-700 text-white",
    },
    info: {
      container:
        "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800",
      icon: "text-blue-500 dark:text-blue-400",
      title: "text-blue-800 dark:text-blue-300",
      message: "text-blue-700 dark:text-blue-400",
      button: "bg-blue-600 hover:bg-blue-700 text-white",
    },
    success: {
      container:
        "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800",
      icon: "text-green-500 dark:text-green-400",
      title: "text-green-800 dark:text-green-300",
      message: "text-green-700 dark:text-green-400",
      button: "bg-green-600 hover:bg-green-700 text-white",
    },
  };

  const variantConfig = variantClasses[variant] || variantClasses.error;

  const content = (
    <div
      className={`rounded-lg border p-6 ${variantConfig.container} ${className}`}
    >
      <div className="flex flex-col items-center text-center">
        <Icon
          name={icon}
          size={48}
          className={`mb-4 ${variantConfig.icon}`}
          strokeWidth={1.5}
        />

        <h3
          className={`font-headline font-semibold text-lg mb-2 ${variantConfig.title}`}
        >
          {title}
        </h3>

        {message && (
          <p className={`mb-6 max-w-md ${variantConfig.message}`}>{message}</p>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {showRetry && onRetry && (
            <Button
              onClick={onRetry}
              className={variantConfig.button}
              leftIcon="RefreshCw"
            >
              {retryText}
            </Button>
          )}

          {actions.map((action, index) => (
            <Button
              key={index}
              onClick={action.onClick}
              variant={action.variant || "outline"}
              leftIcon={action.icon}
            >
              {action.text}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="max-w-md w-full">{content}</div>
      </div>
    );
  }

  return content;
};

// Network error specific component
export const NetworkError = ({ onRetry = null }) => (
  <ErrorMessage
    title="Connection Error"
    message="Unable to connect to the server. Please check your internet connection and try again."
    icon="WifiOff"
    variant="error"
    showRetry={!!onRetry}
    onRetry={onRetry}
    actions={[
      {
        text: "Go Home",
        icon: "Home",
        onClick: () => (window.location.href = "/"),
      },
    ]}
  />
);

// Not found error component
export const NotFoundError = ({ resource = "page", onRetry = null }) => (
  <ErrorMessage
    title={`${resource.charAt(0).toUpperCase() + resource.slice(1)} Not Found`}
    message={`The ${resource} you're looking for doesn't exist or has been moved.`}
    icon="Search"
    variant="info"
    showRetry={!!onRetry}
    onRetry={onRetry}
    actions={[
      {
        text: "Go Back",
        icon: "ArrowLeft",
        onClick: () => window.history.back(),
      },
      {
        text: "Browse Treks",
        icon: "Compass",
        onClick: () => (window.location.href = "/treks"),
      },
    ]}
  />
);

// Access denied error
export const AccessDeniedError = () => (
  <ErrorMessage
    title="Access Denied"
    message="You don't have permission to access this resource."
    icon="Lock"
    variant="warning"
    showRetry={false}
    actions={[
      {
        text: "Sign In",
        icon: "LogIn",
        onClick: () => (window.location.href = "/login"),
      },
      {
        text: "Go Home",
        icon: "Home",
        onClick: () => (window.location.href = "/"),
      },
    ]}
  />
);

export default ErrorMessage;
